import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, X, Phone, ChevronDown, Calendar, User, Users, Stethoscope,
  FileText, ShieldCheck, MessageSquare, HelpCircle
} from 'lucide-react';
import { siteConfig, simplePractice } from '../../data/content';
import BookingModal from '../common/BookingModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { 
      label: 'For You',
      dropdown: [
        { path: '/start-here', label: 'Start Here', icon: HelpCircle, desc: 'New patient? Start here' },
        { path: '/resources', label: 'Patient Education', icon: FileText, desc: 'Learn about dysphagia' },
        { path: '/insurance-info', label: 'Insurance & Billing', icon: ShieldCheck, desc: 'Coverage information' },
      ]
    },
    { path: '/provider-referrals', label: 'For Providers' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200' 
            : 'bg-white/80 backdrop-blur-sm'
        }`}
        role="banner"
      >
        {/* Top Bar - Contact */}
        <div className="hidden md:block bg-teal-700 text-white">
          <div className="container py-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <a 
                href={`tel:${siteConfig.phone}`} 
                className="flex items-center gap-2 hover:text-teal-100 transition-colors"
                data-testid="header-phone"
              >
                <Phone size={14} />
                {siteConfig.phone}
              </a>
              <span className="text-teal-300">|</span>
              <span className="text-teal-100">Greater Houston Area</span>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/start-here" 
                className="text-teal-100 hover:text-white transition-colors"
                data-testid="header-new-patient"
              >
                New Patient?
              </Link>
              {simplePractice.enabled && simplePractice.clientPortalUrl ? (
                <a
                  href={simplePractice.clientPortalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1 rounded-md font-medium transition-colors"
                  data-testid="header-patient-portal"
                >
                  {simplePractice.portalLabel}
                </a>
              ) : (
                <span
                  className="bg-slate-500/50 text-white/80 px-3 py-1 rounded-md text-sm"
                  data-testid="header-patient-portal"
                  title="Patient Portal - Coming Soon"
                >
                  Patient Portal: Coming Soon
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="container">
          <nav className="flex items-center justify-between py-4" aria-label="Main navigation">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 group"
              data-testid="header-logo"
            >
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center group-hover:bg-teal-700 transition-colors">
                <span className="text-white font-bold text-lg">D</span>
              </div>
              <div className="hidden sm:block">
                <span className="font-semibold text-slate-900 text-lg leading-tight block">
                  Dysphagia Therapy
                </span>
                <span className="text-teal-600 text-xs font-medium tracking-wide">
                  PLUS CM, LLC
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link, index) => (
                link.dropdown ? (
                  <div 
                    key={index}
                    className="relative"
                    onMouseEnter={() => setActiveDropdown(index)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <button
                      className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                        ${activeDropdown === index ? 'bg-teal-50 text-teal-700' : 'text-slate-700 hover:bg-slate-100'}`}
                      aria-expanded={activeDropdown === index}
                      aria-haspopup="true"
                      data-testid={`nav-dropdown-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.label}
                      <ChevronDown size={16} className={`transition-transform ${activeDropdown === index ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {activeDropdown === index && (
                      <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-xl shadow-lg border border-slate-200 py-2 animate-slideDown">
                        {link.dropdown.map((item, i) => (
                          <Link
                            key={i}
                            to={item.path}
                            className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors
                              ${isActive(item.path) ? 'bg-teal-50' : ''}`}
                            data-testid={`nav-${item.path.replace('/', '')}`}
                          >
                            <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <item.icon size={16} className="text-teal-700" />
                            </div>
                            <div>
                              <span className="font-medium text-slate-900 block">{item.label}</span>
                              <span className="text-xs text-slate-500">{item.desc}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={index}
                    to={link.path}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                      ${isActive(link.path) 
                        ? 'bg-teal-50 text-teal-700' 
                        : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <a
                href={`tel:${siteConfig.phone}`}
                className="hidden sm:flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 font-medium"
                data-testid="header-call-btn"
              >
                <Phone size={18} />
                <span className="hidden md:inline">Call Now</span>
              </a>
              
              <button
                onClick={() => setShowBookingModal(true)}
                className="btn-accent text-sm py-2.5 px-4 flex items-center gap-2"
                data-testid="header-book-btn"
              >
                <Calendar size={18} />
                <span className="hidden sm:inline">Book Evaluation</span>
                <span className="sm:hidden">Book</span>
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
                aria-expanded={isMenuOpen}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </nav>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg animate-slideDown">
            <div className="container py-4 space-y-1">
              {/* Quick Access Buttons */}
              <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b border-slate-200">
                <Link
                  to="/start-here"
                  className="flex flex-col items-center gap-2 p-3 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                  data-testid="mobile-start-here"
                >
                  <User size={20} />
                  <span className="text-xs font-medium">I Need Therapy</span>
                </Link>
                <Link
                  to="/resources"
                  className="flex flex-col items-center gap-2 p-3 rounded-lg bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="mobile-caregiver"
                >
                  <Users size={20} />
                  <span className="text-xs font-medium">Caregiver</span>
                </Link>
                <Link
                  to="/provider-referrals"
                  className="flex flex-col items-center gap-2 p-3 rounded-lg bg-slate-50 text-slate-700 hover:bg-slate-100 transition-colors"
                  data-testid="mobile-provider"
                >
                  <Stethoscope size={20} />
                  <span className="text-xs font-medium">Provider</span>
                </Link>
              </div>

              {/* Navigation Links */}
              {navLinks.map((link, index) => (
                link.dropdown ? (
                  <div key={index} className="space-y-1">
                    <span className="block px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {link.label}
                    </span>
                    {link.dropdown.map((item, i) => (
                      <Link
                        key={i}
                        to={item.path}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                          ${isActive(item.path) ? 'bg-teal-50 text-teal-700' : 'text-slate-700 hover:bg-slate-100'}`}
                      >
                        <item.icon size={18} />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={index}
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors
                      ${isActive(link.path) ? 'bg-teal-50 text-teal-700' : 'text-slate-700 hover:bg-slate-100'}`}
                  >
                    {link.label}
                  </Link>
                )
              ))}

              {/* Mobile CTA */}
              <div className="pt-4 mt-4 border-t border-slate-200 space-y-3">
                <a
                  href={`tel:${siteConfig.phone}`}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-slate-100 rounded-lg text-slate-700 font-medium"
                >
                  <Phone size={18} />
                  {siteConfig.phone}
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      <BookingModal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} />
    </>
  );
};

export default Header;
